import {
  type ChangeEvent, useEffect, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { skipToken } from '@reduxjs/toolkit/query';
import AppRoutes from 'constants/AppRoutes';
import {
  DESCRIPTION, NAME, STANDARDS, TASK_ASSIGNED_TO, VALUE_CHAIN_MAPS,
} from 'constants/formFields';
import {
  KEY_PERMISSIONS, MATERIALITY_ASSESSMENT_STATUS, TASK_MANAGEMENT_PERMISSIONS,
} from 'constants/general';
import useGetCurrentStandards from 'hooks/useGetCurrentStandards';
import usePillarModulePermission from 'hooks/usePillarModulePermission';
import i18n from 'i18n';
import {
  useCreateMAInitialMutation, useGetSingleMAInitiateQuery,
  useInitializeMAInitialMutation, useUpdateMAMutation, useUpdateUsersJobMAMutation,
} from 'store/slices/materialAssessment/apis/materialAssessmentApi';
import { setMaterialId } from 'store/slices/materialAssessment/slice';
import { useUserMeQuery } from 'store/slices/user/apis/user';

import useInitiateForm from './hooks/useInitiateForm';
import MaterialityAssessmentInitialView from './MaterialityAssessmentInitialView';

import type { MAStatus } from 'store/slices/materialAssessment/interfaces/MAStatus';
import type { IJobPosition } from './interfaces/IJobPosition';
import type { IStandard } from './interfaces/IStandard';

function MaterialityAssessment() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { recordId = '' } = useParams();
  const [activeUserModal, setActiveUserModal] = useState(false);
  const [activeVCModal, setActiveVCModal] = useState(false);
  const [status, setStatus] = useState<MAStatus | undefined>(undefined);

  const fetchQuery = recordId ? {
    id: recordId,
  } : skipToken;
  const { data: singleMA } = useGetSingleMAInitiateQuery(fetchQuery, {
    refetchOnMountOrArgChange: true,
  });

  const [createMAInitial] = useCreateMAInitialMutation();
  const [initializeMAInitial] = useInitializeMAInitialMutation();
  const [updateMaInitial] = useUpdateMAMutation();
  const [updateUsersJobMA] = useUpdateUsersJobMAMutation();

  const { actualStandardOptions } = useGetCurrentStandards();

  const handleStatusChange = (statusType: MAStatus) => {
    setStatus(statusType);
  };

  const { data: userData } = useUserMeQuery();

  const {
    access,
  } = usePillarModulePermission({
    pillarModuleKey: KEY_PERMISSIONS.MATERIALITY_ASSESSMENT_MANAGEMENT,
    userId: userData?.data?.id,
  });

  const isViewOnly = access === TASK_MANAGEMENT_PERMISSIONS.VIEW_ONLY
  || access === TASK_MANAGEMENT_PERMISSIONS.SIGN_OFF
  || singleMA?.data?.status === MATERIALITY_ASSESSMENT_STATUS.APPROVED
  || singleMA?.data?.status === MATERIALITY_ASSESSMENT_STATUS.PENDING_REVIEW;

  const {
    control,
    handleSubmit,
    isDirty,
    setValue,
    watch,
  } = useInitiateForm({
    recordId,
    handleStatusChange,
  });

  const assignedUsers = watch(TASK_ASSIGNED_TO);
  const valueChainMaps = watch(VALUE_CHAIN_MAPS);
  const isNextButtonDisabled = !watch(NAME) || watch(VALUE_CHAIN_MAPS)?.length === 0
  || watch(STANDARDS)?.length === 0
  || !watch(DESCRIPTION);
  const isAddVCButtonDisabled = !!recordId
    && status !== MATERIALITY_ASSESSMENT_STATUS.DRAFT
    && (valueChainMaps?.length || 0) > 0;
  const [isCommentModalVisible, setIsCommentModalVisible] = useState(false);
  const [currentUser, setCurrentUser] = useState('');

  useEffect(() => {
    if (recordId) {
      dispatch(setMaterialId(Number(recordId)));
    }
  }, [dispatch, recordId]);

  const onOpenUserModal = () => {
    if (isViewOnly) {
      return;
    }
    setActiveUserModal(true);
  };

  const onCloseUserModal = () => {
    setActiveUserModal(false);
  };

  const onOpenVCModal = () => {
    if (isViewOnly) {
      return;
    }
    setActiveVCModal(true);
  };

  const onCloseVCModal = () => {
    setActiveVCModal(false);
  };

  const onCancelVCModal = () => {
    onCloseVCModal();
  };
  const goBack = () => {
    navigate(AppRoutes.materialityAssessment);
  };

  const onOpenJobPositionModal = (userId: string) => {
    if (isViewOnly) {
      return;
    }
    setCurrentUser(userId);
    setIsCommentModalVisible(true);
  };

  const onRejectJobPositionModal = () => {
    setIsCommentModalVisible(false);
  };

  const onSubmitJobPositionModal = (comment: string) => {
    if (assignedUsers && assignedUsers.length > 0) {
      const oldArray = [...assignedUsers];
      const newArray = oldArray.map((item) => {
        if (item.value === currentUser) {
          return { ...item, position: comment };
        }
        return item;
      });
      setValue(TASK_ASSIGNED_TO, newArray);
    }

    const id = recordId ? Number(recordId) : undefined;
    const body = {
      teamMembers: watch(TASK_ASSIGNED_TO)?.map((item) => ({
        userId: Number(item.value),
        position: item.position,
      })),
    };
    if (recordId) {
      updateUsersJobMA({
        id: id || 0,
        data: body,
      }).then(() => {
        toast.success(i18n.t('notifications.jobPositionUpdated'));
      }).catch(() => {
        toast.error(i18n.t('notifications.errorJobPositionUpdate'));
      });
    }

    setIsCommentModalVisible(false);
  };

  const onDeleteUser = (userId: string) => {
    if (isViewOnly) {
      return;
    }
    const newArrayUsers = assignedUsers?.filter((user) => user.value !== userId.toString());
    setValue(TASK_ASSIGNED_TO, newArrayUsers);
  };

  const handleStandardChange = (standard: IStandard) => (event: ChangeEvent<HTMLInputElement>) => {
    if (isViewOnly) {
      return;
    }
    const currentStandards = (watch(STANDARDS) || []) as IStandard[];
    if (event.target.checked) {
      setValue(STANDARDS, [...currentStandards, standard], { shouldDirty: true });
    } else {
      setValue(STANDARDS, currentStandards.filter((s) => s.value !== standard.value), { shouldDirty: true });
    }
  };

  const onHandleNext = handleSubmit((data) => {
    // If VIEW_ONLY, just navigate without updating
    if (isViewOnly && recordId) {
      navigate(AppRoutes.materialityAssessmentStakeholders.replace(':recordId', recordId));
      return;
    }

    const id = recordId ? Number(recordId) : undefined;
    const body = {
      name: data[NAME],
      description: data[DESCRIPTION],
      standardIds: data[STANDARDS]?.map((item) => Number(item.value) || 0),
      valueChainMapIds: data[VALUE_CHAIN_MAPS]?.map((item) => item.id || 0),
      teamMembers: data[TASK_ASSIGNED_TO]?.map((item) => ({
        userId: Number(item.value),
        position: item.position,
      })),
    };

    if (recordId && status !== MATERIALITY_ASSESSMENT_STATUS.DRAFT) {
      updateMaInitial({
        id: id || 0,
        data: body,
      }).then(() => {
        toast.success(i18n.t('notifications.materialAssessmentUpdated'));
        navigate(AppRoutes.materialityAssessmentStakeholders.replace(':recordId', recordId));
      }).catch(() => {
        toast.error(i18n.t('notifications.errorMaterialAssessmentUpdate'));
      });
    }

    if (recordId && status === MATERIALITY_ASSESSMENT_STATUS.DRAFT) {
      const initializeAssessment = () => {
        initializeMAInitial({ id: Number(recordId) }).then(() => {
          navigate(AppRoutes.materialityAssessmentStakeholders.replace(':recordId', recordId));
          toast.success(i18n.t('notifications.materialAssessmentCreated'));
        }).catch(() => {
          toast.error(i18n.t('notifications.errorMaterialAssessmentCreate'));
        });
      };

      if (isDirty) {
        updateMaInitial({
          id: id || 0,
          data: body,
        }).then(() => {
          initializeAssessment();
        }).catch(() => {
          toast.error(i18n.t('notifications.errorMaterialAssessmentUpdate'));
        });
      } else {
        initializeAssessment();
      }
    }

    if (!recordId && status !== MATERIALITY_ASSESSMENT_STATUS.DRAFT) {
      createMAInitial(body).then((response) => {
        if ('data' in response && response.data?.data) {
          dispatch(setMaterialId(response.data.data.id));
          initializeMAInitial({ id: Number(response.data.data.id) }).then(() => {
            navigate(AppRoutes.materialityAssessmentStakeholders
              .replace(':recordId', response.data.data.id.toString()));
            toast.success(i18n.t('notifications.materialAssessmentCreated'));
          });
        }
      }).catch(() => {
        toast.error(i18n.t('notifications.errorMaterialAssessmentCreate'));
      });
    }
  });

  const handleDraftSave = handleSubmit((data) => {
    const id = recordId ? Number(recordId) : undefined;
    const body = {
      name: data[NAME],
      description: data[DESCRIPTION],
      standardIds: data[STANDARDS]?.map((item) => Number(item.value) || 0),
      valueChainMapIds: data[VALUE_CHAIN_MAPS]?.map((item) => item.id || 0),
      teamMembers: data[TASK_ASSIGNED_TO]?.map((item) => ({
        userId: Number(item.value),
        position: item.position,
      })),
    };
    if (recordId) {
      updateMaInitial({
        id: id || 0,
        data: body,
      }).then(() => {
        toast.success(i18n.t('notifications.materialAssessmentDraftSaved'));
      }).catch(() => {
        toast.error(i18n.t('notifications.errorMaterialAssessmentDraftSave'));
      });
    } else {
      createMAInitial(body).then(() => {
        toast.success(i18n.t('notifications.materialAssessmentDraftSaved'));
        navigate(AppRoutes.materialityAssessment);
      }).catch(() => {
        toast.error(i18n.t('notifications.errorMaterialAssessmentDraftSave'));
      });
    }
  });

  return (
    <MaterialityAssessmentInitialView
      control={control}
      watch={watch}
      assignedUsers={assignedUsers as IJobPosition[]}
      onOpenUserModal={onOpenUserModal}
      onOpenJobPositionModal={onOpenJobPositionModal}
      onDeleteUser={onDeleteUser}
      handleStandardChange={handleStandardChange}
      goBack={goBack}
      onHandleNext={onHandleNext}
      isNextButtonDisabled={isNextButtonDisabled}
      activeUserModal={activeUserModal}
      onCloseUserModal={onCloseUserModal}
      setValue={setValue}
      isCommentModalVisible={isCommentModalVisible}
      onRejectJobPositionModal={onRejectJobPositionModal}
      onSubmitJobPositionModal={onSubmitJobPositionModal}
      valueChainMaps={valueChainMaps || []}
      onOpenVCModal={onOpenVCModal}
      onCloseVCModal={onCloseVCModal}
      activeVCModal={activeVCModal}
      fieldText={assignedUsers?.find((user) => user.value === currentUser)?.position || ''}
      standardOptions={actualStandardOptions}
      onCancelVCModal={onCancelVCModal}
      handleDraftSave={handleDraftSave}
      draftStatus={status}
      isAddVCButtonDisabled={isAddVCButtonDisabled}
      isViewOnly={isViewOnly}
    />
  );
}

export default MaterialityAssessment;
